import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { Typography } from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
const weightMap = {
  light: 300,
  normal: 400,
  semiBold: 500,
  bold: 600
};
type BaseProps = {
  variant?: 'normal' | 'title' | 'muted' | 'inverted' | 'error' | 'white' | 'mutedDark';
  weight?: keyof typeof weightMap;
  color?: string;
  showEllipsis?: boolean;
  noWrap?: boolean;
  enablePreWrap?: boolean;
  sx?: SxProps<Theme>;
  component?: React.ElementType<any, keyof React.JSX.IntrinsicElements>;
};
export const TextBase = React.forwardRef<HTMLSpanElement, Readonly<React.PropsWithChildren<{
  sizes: [number, number];
} & BaseProps>>>((t0, ref) => {
  const $ = _c(35);
  let children;
  let color;
  let component;
  let props;
  let sizes;
  let t1;
  let t2;
  let t3;
  let variant;
  let weight;
  if ($[0] !== t0) {
    ({
      sizes,
      variant,
      weight,
      children,
      color,
      showEllipsis: t1,
      noWrap: t2,
      component,
      enablePreWrap: t3,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = color;
    $[3] = component;
    $[4] = props;
    $[5] = sizes;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = variant;
    $[10] = weight;
  } else {
    children = $[1];
    color = $[2];
    component = $[3];
    props = $[4];
    sizes = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    variant = $[9];
    weight = $[10];
  }
  const showEllipsis = t1 === undefined ? true : t1;
  const noWrap = t2 === undefined ? false : t2;
  const enablePreWrap = t3 === undefined ? false : t3;
  const theme = useTheme();
  let t4;
  if ($[11] !== theme.palette.common.white || $[12] !== theme.palette.error.main || $[13] !== theme.palette.text.disabled || $[14] !== theme.palette.text.primary) {
    t4 = {
      normal: theme.palette.text.primary,
      white: theme.palette.common.white,
      title: theme.palette.text.primary,
      muted: theme.palette.text.disabled,
      mutedDark: theme.palette.text.disabled,
      inverted: theme.palette.common.white,
      error: theme.palette.error.main
    };
    $[11] = theme.palette.common.white;
    $[12] = theme.palette.error.main;
    $[13] = theme.palette.text.disabled;
    $[14] = theme.palette.text.primary;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const variantMap = t4;
  let t5;
  bb0: {
    if (enablePreWrap) {
      let t6;
      if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = {
          whiteSpace: "pre-wrap",
          wordBreak: "break-word"
        };
        $[16] = t6;
      } else {
        t6 = $[16];
      }
      t5 = t6;
      break bb0;
    }
    let t6;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = {};
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    t5 = t6;
  }
  const wrapRule = t5;
  const t6 = `${sizes[0]}rem`;
  const t7 = `${sizes[1]}rem`;
  const t8 = color || variantMap[variant || "normal"];
  const t9 = weightMap[weight || "normal"];
  const t10 = showEllipsis ? "hidden" : "visible";
  const t11 = showEllipsis ? "ellipsis" : "clip";
  const t12 = noWrap ? "nowrap" : "normal";
  let t13;
  if ($[18] !== t10 || $[19] !== t11 || $[20] !== t12 || $[21] !== t6 || $[22] !== t7 || $[23] !== t8 || $[24] !== t9 || $[25] !== wrapRule) {
    t13 = {
      fontSize: t6,
      lineHeight: t7,
      color: t8,
      fontWeight: t9,
      overflow: t10,
      textOverflow: t11,
      whiteSpace: t12,
      ...wrapRule
    };
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
    $[25] = wrapRule;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== component) {
    t14 = component ? {
      component
    } : {};
    $[27] = component;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== children || $[30] !== props || $[31] !== ref || $[32] !== t13 || $[33] !== t14) {
    t15 = <Typography ref={ref} sx={t13} {...t14} {...props}>{children}</Typography>;
    $[29] = children;
    $[30] = props;
    $[31] = ref;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  return t15;
});
export type TextProps = Readonly<Omit<React.ComponentProps<typeof TextBase>, 'sizes'>>;